import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  ImagePlus,
  MessageSquareQuote,
  Send,
  ShieldCheck,
  Star,
  UploadCloud,
  Video,
  X,
} from "lucide-react";
import { AppDispatch } from "../../../store";
import { useAppSelector } from "../../../Redux/hooks";
import { createTestimonial } from "../../../Redux/custmorTestimonials/slice";
import { createTestimonialDto } from "../../../Redux/custmorTestimonials/types";
import { uploadToCloudinary } from "../../../utility/uploadToCloudinary";
import "./CreateTestimonialPage.css";

const CreateTestimonials = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isLoading } = useAppSelector((state) => state.testimonials);
  const { user } = useAppSelector((state) => state.customerAuth);

  const [form, setForm] = useState<createTestimonialDto>({
    title: "",
    content: "",
    rating: 0,
    image: "",
    video: "",
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState("");
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPreviewOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const displayRating = hoveredRating || Number(form.rating || 0);

  // TEXT + NUMBER HANDLER
  const handleTextChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    if (name === "title") {
      if (value.length > 25) return;
      if (value !== "" && !/^[a-zA-Z0-9&, ]*$/.test(value)) return;
      if (value.includes("  ")) return;
    }

    if (name === "content") {
      if (value.length > 250) return;
      if (value !== "" && !/^[a-zA-Z0-9&,.' @]*$/.test(value)) return;
      if (value.includes("  ")) return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // FILE HANDLER
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    const file = e.target.files?.[0];

    if (!file) return;

    setFieldErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "image") {
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        setFieldErrors((prev) => ({
          ...prev,
          image: "Only PNG, JPG, and JPEG formats are allowed.",
        }));
        e.target.value = "";
        return;
      }
      if (file.size > 500 * 1024) {
        setFieldErrors((prev) => ({
          ...prev,
          image: "Image size must be less than 500KB.",
        }));
        e.target.value = "";
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }

    if (name === "video") {
      if (file.type !== "video/mp4") {
        setFieldErrors((prev) => ({
          ...prev,
          video: "Only MP4 format is allowed.",
        }));
        e.target.value = "";
        return;
      }
      setVideoFile(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview("");
  };

  const removeVideo = () => {
    setVideoFile(null);
  };

  // SUBMIT
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (fieldErrors.image || fieldErrors.video) {
      setError("Please fix the file upload errors before submitting.");
      return;
    }

    if (!form.title || !form.content || !form.rating) {
      setError("Please fill all required fields");
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      setIsUploading(true);
      setError(null);

      let imageUrl = "";
      let videoUrl = "";

      if (imageFile) {
        imageUrl = await uploadToCloudinary(imageFile);
      }
      if (videoFile) {
        videoUrl = await uploadToCloudinary(videoFile);
      }

      const payload: createTestimonialDto = {
        title: form.title,
        content: form.content,
        rating: Number(form.rating),
        image: imageUrl,
        video: videoUrl,
      };

      await dispatch(createTestimonial(payload))
        .unwrap()
        .then(() => {
          setForm({
            title: "",
            content: "",
            rating: 0,
            image: "",
            video: "",
          });
          setImageFile(null);
          setImagePreview("");
          setVideoFile(null);
          setIsUploading(false);
          setSuccess("Testimonial created successfully!");
          setTimeout(() => {
            setSuccess(null);
            navigate("/UserTestimonials");
          }, 2000);
        })
        .catch((err) => {
          setIsUploading(false);
          setError(err.message || "Error creating testimonial");
          setTimeout(() => setError(null), 3000);
        });
    } catch (err: any) {
      setIsUploading(false);
      setError(err.message || "Error uploading media");
      setTimeout(() => setError(null), 3000);
    }
  };

  const authorName =
    user?.fullName || user?.name || user?.registerId || "You";

  return (
    <section className="create-testimonial-page">
      <div className="create-testimonial-shell">
        <header className="create-testimonial-header">
          <div className="create-testimonial-header-left">
            <button
              type="button"
              className="create-testimonial-back"
              onClick={() => navigate("/UserTestimonials")}
              aria-label="Back to testimonials"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="create-testimonial-header-icon">
              <MessageSquareQuote size={22} strokeWidth={2} />
            </div>
            <div>
              <span className="create-testimonial-eyebrow">
                SHARE YOUR EXPERIENCE
              </span>
              <h1>Create Testimonial</h1>
              <p>
                Add your experience, rating and media so it can be shown in
                your testimonial history.
              </p>
            </div>
          </div>
          <div className="create-testimonial-secure">
            <ShieldCheck size={15} />
            Your submission is linked to your Salar account
          </div>
        </header>

        <form className="create-testimonial-layout" onSubmit={handleSubmit}>
          <main className="create-testimonial-card">
            <div className="create-testimonial-card-head">
              <div>
                <span>TESTIMONIAL DETAILS</span>
                <h2>Tell us about your experience</h2>
              </div>
              <div className="create-testimonial-card-icon">
                <Send size={18} />
              </div>
            </div>

            <div className="create-testimonial-fields">
              <label className="create-testimonial-field">
                <div className="create-testimonial-label-row">
                  <span>Title</span>
                  <small>{form.title?.length || 0}/25</small>
                </div>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  maxLength={25}
                  onChange={handleTextChange}
                  placeholder="Give your testimonial a short title"
                />
              </label>

              <label className="create-testimonial-field">
                <div className="create-testimonial-label-row">
                  <span>Message</span>
                  <small>{form.content?.length || 0}/250</small>
                </div>
                <textarea
                  name="content"
                  value={form.content}
                  maxLength={250}
                  onChange={handleTextChange}
                  placeholder="Enter your message here..."
                />
              </label>

              <fieldset className="create-testimonial-rating-field">
                <legend>Rating</legend>
                <div className="create-testimonial-rating-row">
                  <div
                    className="create-testimonial-stars"
                    onMouseLeave={() => setHoveredRating(0)}
                  >
                    {Array.from({ length: 5 }, (_, index) => {
                      const value = index + 1;
                      const active = value <= displayRating;
                      return (
                        <button
                          key={value}
                          type="button"
                          className={active ? "is-active" : ""}
                          onMouseEnter={() => setHoveredRating(value)}
                          onFocus={() => setHoveredRating(value)}
                          onBlur={() => setHoveredRating(0)}
                          onClick={() =>
                            setForm((prev) => ({ ...prev, rating: value }))
                          }
                          aria-label={`Rate ${value} out of 5`}
                        >
                          <Star
                            size={30}
                            strokeWidth={1.8}
                            fill={active ? "currentColor" : "none"}
                          />
                        </button>
                      );
                    })}
                  </div>
                  <div className="create-testimonial-rating-copy">
                    <strong>
                      {form.rating ? `${form.rating}/5` : "Select a rating"}
                    </strong>
                    <span>Tap a star to rate your experience</span>
                  </div>
                </div>
              </fieldset>

              <div className="create-testimonial-upload-section">
                <div className="create-testimonial-section-title">
                  <div>
                    <ImagePlus size={17} />
                    <span>Upload Image</span>
                  </div>
                  <small>JPG or PNG · Max 500 KB</small>
                </div>

                {!imagePreview ? (
                  <label className="create-testimonial-dropzone">
                    <UploadCloud size={27} strokeWidth={1.8} />
                    <strong>Choose testimonial image</strong>
                    <span>Click to browse from your device</span>
                    <input
                      type="file"
                      name="image"
                      accept="image/png, image/jpeg, image/jpg"
                      onChange={handleFileChange}
                    />
                  </label>
                ) : (
                  <div className="create-testimonial-image-preview">
                    <img src={imagePreview} alt="Selected testimonial" />
                    <div className="create-testimonial-image-overlay">
                      <button
                        type="button"
                        onClick={() => setPreviewOpen(true)}
                      >
                        <Eye size={17} />
                        Preview
                      </button>
                    </div>
                    <button
                      type="button"
                      className="create-testimonial-remove-image"
                      onClick={removeImage}
                      aria-label="Remove image"
                    >
                      <X size={16} />
                    </button>
                    <span className="create-testimonial-image-name">
                      {imageFile?.name}
                    </span>
                  </div>
                )}
                {fieldErrors.image && (
                  <div
                    className="create-testimonial-message is-error"
                    role="alert"
                  >
                    {fieldErrors.image}
                  </div>
                )}
              </div>

              <div className="create-testimonial-upload-section">
                <div className="create-testimonial-section-title">
                  <div>
                    <Video size={17} />
                    <span>Upload Video (optional)</span>
                  </div>
                  <small>MP4 format only</small>
                </div>

                {!videoFile ? (
                  <label className="create-testimonial-dropzone">
                    <UploadCloud size={27} strokeWidth={1.8} />
                    <strong>Choose testimonial video</strong>
                    <span>Click to browse MP4 from your device</span>
                    <input
                      type="file"
                      name="video"
                      accept="video/mp4"
                      onChange={handleFileChange}
                    />
                  </label>
                ) : (
                  <div className="create-testimonial-image-preview">
                    <div className="create-testimonial-preview-placeholder">
                      <Video size={26} />
                      <span>{videoFile.name}</span>
                    </div>
                    <button
                      type="button"
                      className="create-testimonial-remove-image"
                      onClick={removeVideo}
                      aria-label="Remove video"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
                {fieldErrors.video && (
                  <div
                    className="create-testimonial-message is-error"
                    role="alert"
                  >
                    {fieldErrors.video}
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="create-testimonial-message is-error" role="alert">
                {error}
              </div>
            )}
            {success && (
              <div
                className="create-testimonial-message is-success"
                role="status"
              >
                <CheckCircle2 size={16} />
                {success}
              </div>
            )}

            <button
              type="submit"
              className="create-testimonial-submit"
              disabled={isLoading || isUploading}
            >
              <Send size={17} />
              {isUploading
                ? "Uploading Media..."
                : isLoading
                  ? "Submitting..."
                  : "Submit Testimonial"}
            </button>
          </main>

          <aside className="create-testimonial-side">
            <div className="create-testimonial-side-icon">
              <Video size={21} />
            </div>
            <span>LIVE PREVIEW</span>
            <h3>Your testimonial</h3>
            <div className="create-testimonial-preview-card">
              <div className="create-testimonial-preview-media">
                {imagePreview ? (
                  <img src={imagePreview} alt="Testimonial preview" />
                ) : (
                  <div className="create-testimonial-preview-placeholder">
                    <ImagePlus size={28} />
                    <span>Image preview</span>
                  </div>
                )}
              </div>
              <div className="create-testimonial-preview-body">
                <h4>{form.title?.trim() || "Your testimonial title"}</h4>
                <div className="create-testimonial-preview-stars">
                  {Array.from({ length: 5 }, (_, index) => (
                    <Star
                      key={index}
                      size={14}
                      fill={
                        index < Number(form.rating || 0)
                          ? "currentColor"
                          : "none"
                      }
                      className={
                        index < Number(form.rating || 0) ? "is-active" : ""
                      }
                    />
                  ))}
                </div>
                <p>
                  {form.content?.trim() ||
                    "Your testimonial message will appear here while you type."}
                </p>
              </div>
            </div>
            <div className="create-testimonial-side-note">
              <ShieldCheck size={15} />
              Posted as {authorName}. Review your text, rating and image
              before submitting.
            </div>
          </aside>
        </form>
      </div>

      {previewOpen && imagePreview && (
        <div
          className="create-testimonial-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setPreviewOpen(false);
            }
          }}
        >
          <div className="create-testimonial-modal-card">
            <button
              type="button"
              className="create-testimonial-modal-close"
              onClick={() => setPreviewOpen(false)}
              aria-label="Close preview"
            >
              <X size={20} />
            </button>
            <img src={imagePreview} alt="Full testimonial preview" />
          </div>
        </div>
      )}
    </section>
  );
};

export default CreateTestimonials;
